import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Calendar, MapPin, Image as ImageIcon, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { getOptimizedImageUrl } from "@/utils/imageUtils";
import { cn } from "@/lib/utils";
import type { EventWithImages } from "@/hooks/useGalleryEvents";
import type { GalleryImage } from "@/types/gallery";

interface EventPreviewDialogProps {
  event: EventWithImages | null;
  language: string;
  images: GalleryImage[];
  currentImageIndex: number;
  onClose: () => void;
  onPreviousImage: () => void;
  onNextImage: () => void;
  onSelectImage: (index: number) => void;
}

const EventPreviewDialog = ({ event, language, images, currentImageIndex, onClose, onPreviousImage, onNextImage, onSelectImage }: EventPreviewDialogProps) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState<{ x: number; y: number; panX: number; panY: number } | null>(null);

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setDragStart(null);
  }, [currentImageIndex]);

  if (!event) return null;

  const title = language === "KO" && event.title_ko ? event.title_ko : event.title_en;
  const description = language === "KO" && event.description_ko ? event.description_ko : event.description_en;
  const currentImage = images[currentImageIndex];
  const caption = currentImage
    ? language === "KO" && currentImage.caption_ko
      ? currentImage.caption_ko
      : currentImage.caption_en
    : null;

  const updateZoom = (nextZoom: number) => {
    const clampedZoom = Math.min(3, Math.max(1, nextZoom));
    setZoom(clampedZoom);
    if (clampedZoom === 1) setPan({ x: 0, y: 0 });
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragStart({ x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart || zoom <= 1) return;
    setPan({
      x: dragStart.panX + event.clientX - dragStart.x,
      y: dragStart.panY + event.clientY - dragStart.y,
    });
  };

  const stopDragging = () => setDragStart(null);

  return (
    <Dialog open={!!event} onOpenChange={onClose}>
      <DialogContent className="h-[100svh] max-h-[100svh] w-screen max-w-none overflow-y-auto rounded-none bg-white p-0 shadow-2xl sm:h-[92vh] sm:max-h-[92vh] sm:w-[96vw] sm:max-w-7xl sm:rounded-2xl [&>button]:z-50 [&>button]:rounded-full [&>button]:bg-white/90 [&>button]:p-2 [&>button]:text-gray-900 [&>button]:shadow-md">
        <div className="grid min-h-full bg-white lg:grid-cols-[minmax(0,1.55fr)_420px]">
          <div className="flex min-h-[55svh] flex-col bg-gradient-to-br from-neutral-950 via-neutral-900 to-primary/90 lg:min-h-full">
            <div
              className={cn(
                "relative flex flex-1 touch-none items-center justify-center overflow-hidden p-3 sm:p-6",
                zoom > 1 ? (dragStart ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in"
              )}
              onDoubleClick={() => updateZoom(zoom === 1 ? 2 : 1)}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={stopDragging}
              onPointerCancel={stopDragging}
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08),transparent_36%)]" />
              {currentImage ? (
                <img
                  src={getOptimizedImageUrl(currentImage.image_url, { width: 1400, quality: 85 })}
                  alt={currentImage.alt_text || title}
                  className="relative z-10 max-h-[55svh] w-full object-contain drop-shadow-2xl lg:max-h-[72vh]"
                  style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
                  loading="eager"
                  decoding="async"
                  draggable={false}
                />
              ) : (
                <div className="p-8 text-center text-white">No images available</div>
              )}

              {currentImage && (
                <div className="absolute left-4 top-4 z-20 flex items-center gap-2 rounded-full bg-black/60 p-1 shadow-lg backdrop-blur" onPointerDown={(event) => event.stopPropagation()}>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full text-white hover:bg-white/15" onClick={(event) => { event.stopPropagation(); updateZoom(zoom - 0.5); }} disabled={zoom <= 1}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="min-w-10 text-center text-xs font-medium text-white">{Math.round(zoom * 100)}%</span>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full text-white hover:bg-white/15" onClick={(event) => { event.stopPropagation(); updateZoom(zoom + 0.5); }} disabled={zoom >= 3}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full text-white hover:bg-white/15" onClick={(event) => { event.stopPropagation(); updateZoom(1); }} disabled={zoom === 1}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {images.length > 1 && (
                <>
                  <Button variant="ghost" size="icon" className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/60 text-white shadow-lg backdrop-blur hover:bg-black/80" onClick={onPreviousImage}>
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button variant="ghost" size="icon" className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/60 text-white shadow-lg backdrop-blur hover:bg-black/80" onClick={onNextImage}>
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                </>
              )}

              {images.length > 0 && (
                <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 rounded-full bg-black/70 px-4 py-2 text-sm text-white shadow-lg backdrop-blur">
                  {currentImageIndex + 1} / {images.length}
                </div>
              )}
            </div>

            {(caption || images.length > 1) && (
              <div className="border-t border-white/10 bg-black/45 p-4 backdrop-blur-sm">
                {caption && <p className="mx-auto mb-3 max-w-4xl text-center text-sm leading-relaxed text-white/90 sm:text-base">“{caption}”</p>}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
                    {images.map((image, index) => (
                      <button
                        key={image.id || image.image_url}
                        onClick={() => onSelectImage(index)}
                        className={cn("relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border transition", index === currentImageIndex ? "border-white opacity-100 ring-2 ring-white" : "border-white/20 opacity-65 hover:opacity-100")}
                      >
                        <img src={getOptimizedImageUrl(image.image_url, { width: 180, quality: 70 })} alt={image.alt_text || title} className="h-full w-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="flex flex-col border-l border-gray-100 bg-gradient-to-b from-white to-gray-50/60">
            <div className="border-b border-gray-100 p-5 sm:p-6">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="bg-primary/10 text-primary ring-1 ring-primary/10">{event.category}</Badge>
                {images.length > 0 && <Badge variant="secondary" className="gap-1 bg-gray-100 text-gray-700"><ImageIcon className="h-3.5 w-3.5" />{images.length} photos</Badge>}
              </div>
              <h2 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl">{title}</h2>
              <div className="mt-4 space-y-3 text-sm text-gray-600">
                <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" />{new Date(event.event_date).toLocaleDateString()}</div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />{event.location}</div>
              </div>
            </div>
            <div className="flex-1 p-5 sm:p-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Event Description</h3>
              <p className="whitespace-pre-line text-base leading-7 text-gray-700">{description}</p>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventPreviewDialog;
